
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { generateFollowUpMessage } from "@/api/aiAgents.api.js";
import { getContentMediaStatus, uploadLibraryMedia } from "@/api/ghlContent.api.js";
import {
  MAX_DELAY_HOURS,
  MAX_FOLLOW_UP_STEPS,
  clampDelayMinutes,
  delayDisplayValue,
  delayInputMax,
  delayInputMin,
  exceedsSocialWindow,
  inferDelayUnit,
  toDelayMinutes
} from "@/utils/followUpPolicy.js";
import ContentMediaLibrary from "@/components/social/ContentMediaLibrary.vue";

type StepMediaLibraryRef = {
  loadMedia?: () => Promise<void>;
  getFileForId?: (id: number) => any;
  isImage?: (file: any) => boolean;
} | null;

const DEFAULT_STEP = () => ({
  delayMinutes: 15,
  delayUnit: "minutes" as "minutes" | "hours",
  useLlm: true,
  message: "",
  llmHint: "",
  mediaIds: [] as number[]
});

export default defineComponent({
  name: "ai-agent-follow-up-panel",
  components: {
    ContentMediaLibrary
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    agentId: {
      type: Number,
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    },
    agentOptions: {
      type: Object,
      default: () => ({
        contactUpdate: { askForEmail: false, askForPhone: false },
        followUp: { continueOnEmail: false, includeColdLeads: false }
      })
    }
  },
  emits: ["update:modelValue", "update:agentOptions"],
  data() {
    return {
      selectedScenario: "contactStoppedReplying",
      generatingIndex: null as number | null,
      uploadingStepIndex: null as number | null,
      followUpUploadStepIndex: null as number | null,
      stepMediaLibraries: {} as Record<number, StepMediaLibraryRef>,
      collapsedStepKeys: {} as Record<string, boolean>,
      scenarioItems: [
        { key: "contactStoppedReplying", label: "Conversation in-activity", disabled: false }
      ]
    };
  },
  computed: {
    scenarios(): any {
      return this.modelValue;
    },
    activeScenario(): any {
      return this.scenarios[this.selectedScenario] || this.scenarios.contactStoppedReplying;
    },
    windowWarning() {
      if (!this.activeScenario || !this.activeScenario.steps || !this.activeScenario.steps.length) {
        return "";
      }
      if (exceedsSocialWindow(this.activeScenario.steps)) {
        return "Total follow-up delays exceed 20 hours. Facebook, Instagram, and WhatsApp may block messages sent outside the messaging window.";
      }
      return "";
    },
    followUpOptions(): { continueOnEmail: boolean; includeColdLeads: boolean } {
      const followUp = this.agentOptions?.followUp || {};
      return {
        continueOnEmail: Boolean(followUp.continueOnEmail),
        includeColdLeads: Boolean(followUp.includeColdLeads)
      };
    },
    askForEmailWarning(): boolean {
      return this.followUpOptions.continueOnEmail
        && !Boolean(this.agentOptions?.contactUpdate?.askForEmail);
    },
    maxFollowUpSteps(): number {
      return MAX_FOLLOW_UP_STEPS;
    }
  },
  methods: {
    stepCollapseKey(index: number) {
      return `${this.selectedScenario}-${index}`;
    },
    isStepCollapsed(index: number) {
      return Boolean(this.collapsedStepKeys[this.stepCollapseKey(index)]);
    },
    toggleStepCollapsed(index: number) {
      const key = this.stepCollapseKey(index);
      this.collapsedStepKeys[key] = !this.collapsedStepKeys[key];
    },
    setFollowUpOption(key: "continueOnEmail" | "includeColdLeads", value: boolean) {
      this.$emit("update:agentOptions", {
        ...this.agentOptions,
        followUp: {
          ...this.followUpOptions,
          [key]: value
        }
      });
    },
    delayInputMin,
    delayInputMax,
    getStepDelayUnit(step: any) {
      return step.delayUnit || inferDelayUnit(step.delayMinutes);
    },
    getDelayDisplayValue(step: any) {
      return delayDisplayValue(step.delayMinutes, this.getStepDelayUnit(step));
    },
    onDelayValueChange(step: any, event: Event) {
      const target = event.target as HTMLInputElement;
      step.delayMinutes = toDelayMinutes(target.value, this.getStepDelayUnit(step));
    },
    onDelayUnitChange(step: any, event: Event) {
      const target = event.target as HTMLSelectElement;
      const newUnit = target.value as "minutes" | "hours";
      const oldUnit = this.getStepDelayUnit(step);
      if (newUnit === oldUnit) return;

      step.delayUnit = newUnit;
      if (newUnit === "hours") {
        const hours = Math.max(
          1,
          Math.min(MAX_DELAY_HOURS, Math.round((step.delayMinutes || 15) / 60))
        );
        step.delayMinutes = hours * 60;
      } else {
        step.delayMinutes = clampDelayMinutes(step.delayMinutes);
      }
    },
    stepMediaIds(step: any) {
      return Array.isArray(step.mediaIds) ? step.mediaIds : [];
    },
    hasStepMedia(step: any) {
      return this.stepMediaIds(step).length > 0;
    },
    onUseLlmChange(step: any) {
      if (step.useLlm) {
        step.mediaIds = [];
      } else if (!Array.isArray(step.mediaIds)) {
        step.mediaIds = [];
      }
    },
    selectScenario(item: any) {
      if (item.disabled) return;
      this.selectedScenario = item.key;
    },
    onScenarioEnabledChange(key: string) {
      const scenario = this.scenarios[key];
      if (scenario.enabled && !scenario.steps.length) {
        scenario.steps.push(DEFAULT_STEP());
      }
    },
    addStep() {
      if (this.activeScenario.steps.length >= MAX_FOLLOW_UP_STEPS) return;
      this.activeScenario.steps.push(DEFAULT_STEP());
      const index = this.activeScenario.steps.length - 1;
      this.collapsedStepKeys[this.stepCollapseKey(index)] = false;
    },
    removeStep(index: number) {
      const scenarioKey = this.selectedScenario;
      const prev = { ...this.collapsedStepKeys };
      this.activeScenario.steps.splice(index, 1);
      const next: Record<string, boolean> = {};
      for (let i = 0; i < this.activeScenario.steps.length; i += 1) {
        const sourceIndex = i >= index ? i + 1 : i;
        const oldKey = `${scenarioKey}-${sourceIndex}`;
        if (prev[oldKey]) {
          next[`${scenarioKey}-${i}`] = prev[oldKey];
        }
      }
      this.collapsedStepKeys = next;
    },
    updateStepMediaIds(index: number, mediaIds: number[]) {
      this.activeScenario.steps[index].mediaIds = mediaIds;
    },
    setStepMediaLibraryRef(index: number, el: unknown) {
      if (el) {
        this.stepMediaLibraries[index] = el as StepMediaLibraryRef;
      }
    },
    openFollowUpUpload(index: number) {
      this.followUpUploadStepIndex = index;
      const input = this.$refs.followUpUploadInput as HTMLInputElement | undefined;
      input?.click();
    },
    async onFollowUpFileSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      input.value = "";
      const stepIndex = this.followUpUploadStepIndex;
      this.followUpUploadStepIndex = null;
      if (!file || stepIndex === null || stepIndex === undefined) {
        return;
      }

      this.uploadingStepIndex = stepIndex;
      try {
        const response = await uploadLibraryMedia(file);
        const uploaded = response.data || {};
        let media = uploaded;
        if (media.status !== "ready") {
          media = await this.pollLibraryMediaReady(Number(media.id));
        }
        await this.attachUploadedMediaToStep(stepIndex, media);
      } catch (err) {
        Swal.fire({
          text: (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
            || (err as Error)?.message
            || "Unable to upload file.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      } finally {
        this.uploadingStepIndex = null;
      }
    },
    async pollLibraryMediaReady(mediaId: number, attempts = 30) {
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        const response = await getContentMediaStatus(mediaId);
        const media = response.data;
        if (media?.status === "ready") {
          return media;
        }
        if (media?.status === "failed") {
          throw new Error("Upload failed");
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      throw new Error("Upload timed out");
    },
    async attachUploadedMediaToStep(stepIndex: number, media: any) {
      const library = this.stepMediaLibraries[stepIndex];
      await library?.loadMedia?.();

      const mediaId = Number(media.id);
      const step = this.activeScenario.steps[stepIndex];
      const ids = this.stepMediaIds(step).filter((id) => id !== mediaId);

      if (ids.length >= 4) {
        Swal.fire({
          text: "You can attach at most 4 files per follow-up.",
          icon: "info",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
        return;
      }

      step.mediaIds = [...ids, mediaId];
    },
    async generateMessage(index: number) {
      if (!this.agentId) return;
      this.generatingIndex = index;
      try {
        const step = this.activeScenario.steps[index];
        const response = await generateFollowUpMessage(this.agentId, {
          llmHint: step.llmHint || step.message || undefined
        });
        this.activeScenario.steps[index].message = response.data?.message || "";
      } catch (err) {
        console.error(err);
      } finally {
        this.generatingIndex = null;
      }
    }
  }
});
